import React from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { getProducts } from '../../redux/slices/content'
import RelatedProductCard from '../RelatedProductCard'
import theme from '../../style/theme'

const RelatedProductsSlice = ({ slice, className }) => {
  const classes = useStyles()
  const allProducts = useSelector(getProducts)

  if (!slice) {
    console.error('RelatedProductsSlice: No slice data provided')
    return null
  }

  // Extract data from slice
  const title = get(slice, 'title') || 'You might also like'
  const manualProducts = get(slice, 'products', [])
  const currentProductId = get(slice, 'currentProductId') // To exclude current product
  const limit = get(slice, 'limit', 3)

  // console.log('RelatedProductsSlice: Received data:', {
  //   title,
  //   manualProductsCount: manualProducts.length,
  //   currentProductId,
  //   manualProductsData: manualProducts
  // })

  if (!allProducts) {
    return (
      <div className={classes.container}>
        <h2 className={classes.title}>{title}</h2>
        <div className={classes.grid}>
          {[...Array(limit)].map((_, i) => (
            <div key={i} className={classes.skeleton} />
          ))}
        </div>
      </div>
    )
  }

  let productsToShow = []

  // Only show manually selected products - no automatic suggestions
  if (!isEmpty(manualProducts)) {
    // Map manual product references to actual product data
    productsToShow = manualProducts
      .map(productRef => {
        if (!productRef) return null
        // Handle different reference formats
        const productId = productRef._id || productRef.id || productRef._ref
        if (!productId) return null
        // Find the actual product data
        return allProducts.find(product =>
          product._id === productId ||
          product.id === productId ||
          product._ref === productId
        )
      })
      .filter(Boolean) // Remove null/undefined entries
      .filter(product => {
        // Exclude current product if it matches
        const productId = product._id || product.id
        return productId !== currentProductId
      })
  }

  // No automatic product filling - only show manually selected products

  // console.log('RelatedProductsSlice: Showing only manually selected products:', productsToShow.length)

  if (!productsToShow.length) {
    return null
  }

  return (
    <div className={cn(classes.container, className)}>
      <h2 className={classes.title}>{title}</h2>
      <div className={classes.grid}>
        {productsToShow.map((product) => (
          <RelatedProductCard
            key={product._id || product.id}
            product={product}
            imageMaxHeightClass={classes.relatedProductImageMaxHeight}
          />
        ))}
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    marginTop: '150px',
    marginBottom: '108px',
    // maxWidth: 'calc(100% - 9rem)',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      marginTop: '110px',
      marginBottom: '58px'
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      paddingLeft: '20px',
      paddingRight: '20px'
    }
  },
  title: {
    fontSize: '48px',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    marginBottom: '60px',
    color: theme.colors.primary,
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      fontSize: '30px',
      marginBottom: '40px'
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: '20px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '19px'
    }
  },
  relatedProductImageMaxHeight: {
    maxHeight: '331px'
  },
  skeleton: {
    height: '400px',
    backgroundColor: theme.colors.lightGrey,
    borderRadius: '8px',
    animation: '$pulse 1.5s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 0.8 },
    '100%': { opacity: 0.6 }
  }
}, { name: 'RelatedProductsSlice' })

export default RelatedProductsSlice
